import { Button } from "@mui/material";
import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { SefanoButton } from "@/components/Molecules";
import { StatusPageTemplate } from "@/components/Templates/StatusPage";
import { normalizeApiError } from "@/lib/api/errors";
import {
  getStatusPageVariant,
  STATUS_PAGES,
  type StatusPageVariant,
} from "@/lib/errors/statusPages";
import * as styles from "./ErrorPage.page.styles";

type ErrorPageProps = {
  variant: StatusPageVariant;
  onRetry?: () => void;
  overlay?: boolean;
};

export function ErrorPage({ variant, onRetry, overlay }: ErrorPageProps) {
  const router = useRouter();
  const content = STATUS_PAGES[variant];

  const retry = onRetry ?? (() => window.location.reload());
  const goHome = () => void router.navigate({ to: "/" });
  const goBack = () => (window.history.length > 1 ? router.history.back() : goHome());
  const goLogin = () =>
    void router.navigate({ to: "/login", search: { redirect: router.state.location.href } });

  const primary = (label: string, onClick: () => void) => (
    <SefanoButton onClick={onClick} sx={styles.primaryAction}>
      {label}
    </SefanoButton>
  );
  const link = (label: string, onClick: () => void) => (
    <Button variant="text" fullWidth={false} onClick={onClick} sx={styles.linkAction}>
      {label}
    </Button>
  );

  const retryButton = primary("تلاش دوباره", retry);
  const homeButton = primary("بازگشت به داشبورد", goHome);
  const homeLink = link("بازگشت به داشبورد", goHome);
  const backLink = link("صفحه قبل", goBack);

  const actions: Record<StatusPageVariant, ReactNode> = {
    "400": (
      <>
        {retryButton}
        {homeLink}
      </>
    ),
    "401": primary("ورود به حساب", goLogin),
    "403": (
      <>
        {homeButton}
        {backLink}
      </>
    ),
    "404": (
      <>
        {homeButton}
        {backLink}
      </>
    ),
    "500": (
      <>
        {retryButton}
        {homeLink}
      </>
    ),
    "503": retryButton,
    offline: retryButton,
    comingSoon: (
      <>
        {homeButton}
        {backLink}
      </>
    ),
  };

  return <StatusPageTemplate {...content} overlay={overlay} actions={actions[variant]} />;
}

/** Router-wide `defaultErrorComponent`: a crash in one page never blanks the whole app. */
export function RouteErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const { kind, status } = normalizeApiError(error);

  useEffect(() => {
    console.error(error);
  }, [error]);

  const handleRetry = () => {
    reset();
    void router.invalidate();
  };

  return (
    <ErrorPage
      variant={getStatusPageVariant(status, kind === "network" || kind === "timeout")}
      onRetry={handleRetry}
    />
  );
}

export function NotFoundPage() {
  return <ErrorPage variant="404" />;
}
